import React, { Component } from 'react';
import {
  Button,
  Paper,
  Typography,
  TextField,
  Container,
  Divider,
  FormControl,
  InputAdornment,
  CircularProgress,
  Box,
  IconButton
} from '@mui/material';
import ErrorModal from './ErrorModal';
import ImagePreviewModal from './ImagePreviewModal';
import RecommendationSelection from './RecommendationSelection';
import ChatOptionsDialog from './ChatOptionsDialog/ChatOptionsDialog';
import ComposeChatDialog from './ComposeChatDialog/ComposeChatDialog';
import MessageEditDialog from './MessageEditDialog/MessageEditDialog';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { ChevronLeft } from '@mui/icons-material';
import packageJson from '../../package.json';
import {
  isExpired,
  isPopupBlockerEnabled,
  safeJsonParse,
  parseMessageTimestamp,
  formatDateDivider
} from '../components/utils/helpers';
import { ChatMessage } from './chat/chat-message';
import { DraftContext } from '../contexts/DraftContext';
import {
  fetchChatMessages,
  fetchOlderChatHistory,
  sendChatMessage,
  editChatMessage,
  deleteChatMessage
} from '../services/chatApi';

const secureURL = process.env.REACT_APP_SECURE_URL;
const secureKey = process.env.REACT_APP_UPLOAD_API_KEY;
const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const fileTypeMap = {
  'image/jpeg': 'JPEG',
  'image/jpg': 'JPG',
  'image/png': 'PNG',
  'image/gif': 'GIF',
  'image/webp': 'WEBP',
  'video/mp4': 'MP4',
  'video/3gpp': '3GPP',
  'audio/mpeg': 'MPEG',
  'audio/ogg': 'OGG',
  'audio/opus': 'OPUS',
  'application/pdf': 'PDF',
  'application/msword': 'MSWORD',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'WORD',
  'application/vnd.ms-excel': 'EXCEL',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'EXCEL',
  'application/vnd.ms-powerpoint': 'PPT',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPT',
  'text/plain': 'TXT',
  'application/rtf': 'RTF',
  'text/csv': 'CSV',
  'text/vcard': 'VCARD',
  'application/zip': 'ZIP'
};

class Chat extends Component {
  static contextType = DraftContext;
  isSecretChat = () => {
    if (this.props.chatTask) {
      if (
        (this.props.chatTask.fields && this.props.chatTask.fields['Secret Task']) ||
        (this.props.chatTask.id && this.props.chatTask.id.startsWith('mem_'))
      ) {
        return true;
      }
    }
    return false;
  };

  constructor(props) {
    super(props);
    this.state = {
      chatMessages: [],
      userMessage: '',
      messageData: '',
      userId: this.props.userId,
      familyId: this.props.familyID,
      taskId: this.props.chatTask ? this.props.chatTask.id : null,
      memberId: null,
      isSecretTask: this.isSecretChat(),
      members: this.props.members,
      retryCount: 0,
      isChatOptionsOpen: false,
      isComposeChatOpen: false,
      showOldMessages: false,
      currentOffset: 30,
      selectedMessage: null,
      isMessageEditDialogOpen: false,
      isMessageSending: false,
      isEditingMessage: false,
      editingMessageID: null,
      selectedFile: null,
      errorMessage: null,
      fileTypeFormatted: '',
      fileType: '',
      fileName: '',
      fileSize: '',
      filePreview: null,
      image: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
      document: [
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'text/plain',
        'application/rtf',
        'text/csv',
        'text/vcard',
        'application/zip'
      ],
      video: ['video/mp4', 'video/3gpp'],
      audio: ['audio/mpeg', 'audio/ogg', 'audio/opus'],
      showRecommendationSelection: false,
      shouldMarkAsRead: false,
      recommendationCache: {}, // Cache for recommendation data { recommendation_id: { data, loading } }
      imagePreviewUrl: null,
      imagePreviewItem: null,
      downloadingDocumentId: null, // Track which document is being downloaded
      loadingAudioId: null, // Track which audio is being loaded
      searchQuery: '',
      isSearchMode: false,
      searchResults: [],
      searchOffset: 0,
      isSearching: false,
      showSearchInput: false
    };
    this.inputRef = React.createRef();
    this.messageContainerRef = React.createRef();
    this.lastDate = null;
    this.handleMediaOpen = this.handleMediaOpen.bind(this);
  }

  messagesEndRef = React.createRef();

  componentDidMount() {
    this.scrollToBottom();
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (!this.props.chatTask) return;

    if (this.props.chatTask?.id !== prevProps.chatTask?.id) {
      this.setState({ showRecommendationSelection: false });
      this.scrollToBottom();
      let memberId = null;
      if (
        this.props.chatTask &&
        this.props.chatTask.fields &&
        this.props.chatTask.fields['Member']
      ) {
        memberId = this.props.chatTask.fields['Member'][0];
      }

      if (this.props.chatTask && this.props.chatTask.id.startsWith('mem_')) {
        memberId = this.props.chatTask.id.replace('mem_', '');
      }

      if (prevProps.chatTask && prevState.userMessage && !this.state.isEditingMessage) {
        const prevTaskId = prevProps.chatTask.id;
        this.context.saveDraft(prevTaskId, prevState.userMessage);
      }

      // const newTaskId = this.props.chatTask ? this.props.chatTask.id : 'General';
      const newTaskId = this.props.chatTask?.id;
      const loadedDraft = this.context.getDraft(newTaskId);

      this.setState(
        {
          shouldMarkAsRead: true,
          taskId: this.props.chatTask ? this.props.chatTask.id : 'General',
          isSecretTask: this.isSecretChat(),
          members: this.props.members,
          memberId: memberId,
          currentOffset: 30,
          userMessage: loadedDraft,
          isEditingMessage: false,
          editingMessageID: null,
          referencedMessage: null,
          searchQuery: '',
          isSearchMode: false,
          searchResults: [],
          searchOffset: 0,
          showSearchInput: false
        },
        async () => {
          try {
            await this.fetchChatMessages();
          } catch (error) {
            console.error('Error fetching chat messages:', error);
          }
        }
      );
    }

    if (prevProps.chatMessages !== this.props.chatMessages) {
      if (!this.state.showOldMessages) {
        this.scrollToBottom();
        if (this.state.shouldMarkAsRead && this.props.chatMessages.length > 0) {
          const unreadMessages = this.props.chatMessages.filter((msg) => {
            return !msg.user_id && !msg.timestamp_read;
          });

          if (unreadMessages.length > 0) {
            this.props.sendReadAcknowledgementType(
              unreadMessages,
              'admin',
              this.state.familyId,
              this.state.taskId
            );
          }
          this.setState({ shouldMarkAsRead: false });
        }
      }
    }
    if (!this.props.isWebSocketOpen) {
      console.error('WebSocket is not connected. Attempting to reconnect from chat...');
      this.props.connectWebSocket && this.props.connectWebSocket();
    }
  }

  componentWillUnmount() {
    if (this.props.isWebSocketOpen) {
      this.props.ws.removeEventListener('message', this.handleMessageConfirmation);
    }
  }

  scrollToBottom = () => {
    if (this.messagesEndRef.current) {
      this.messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
    }
  };

  fetchChatMessages = async () => {
    try {
      const { taskId, familyId } = this.state;
      const messages = await fetchChatMessages(familyId, taskId);

      this.props.onChatMessagesUpdated && this.props.onChatMessagesUpdated(messages);
    } catch (error) {
      console.error('Error fetching chat messages:', error);
      this.setState({
        errorMessage: 'Failed to load chat messages. Please try again.'
      });
    }
  };

  handleSearchChange = (e) => {
    const newQuery = e.target.value;

    // Reset search mode if user changes the keyword
    this.setState(
      {
        searchQuery: newQuery,
        isSearchMode: false,
        searchResults: [],
        searchOffset: 0
      },
      () => {
        this.scrollToBottom();
      }
    );
  };

  handleSearchSubmit = async () => {
    const { taskId, familyId, searchQuery } = this.state;
    if (!searchQuery.trim()) return;

    this.setState({ isSearching: true });

    try {
      const results = await fetchOlderChatHistory(familyId, taskId, 0, searchQuery.trim());
      console.log('Search results:', results);
      this.setState({
        isSearchMode: true,
        searchResults: results,
        searchOffset: 30,
        isSearching: false
      });
    } catch (error) {
      console.error('Error searching messages:', error);
      this.setState({
        errorMessage: 'Failed to search messages. Please try again.',
        isSearching: false
      });
    }
  };

  handleClearSearch = () => {
    this.setState(
      {
        searchQuery: '',
        isSearchMode: false,
        searchResults: [],
        searchOffset: 0,
        showSearchInput: false
      },
      () => {
        this.scrollToBottom();
      }
    );
  };

  toggleSearchInput = () => {
    this.setState((prevState) => ({
      showSearchInput: !prevState.showSearchInput,
      searchQuery: '',
      isSearchMode: false,
      searchResults: [],
      searchOffset: 0
    }));
  };

  // fetchChatHistory = async () => {
  //   if (!this.props.isWebSocketOpen) {
  //     throw new Error('WebSocket is not open.');
  //   }
  //   try {
  //     await this.requestChatHistory();
  //     // this.sendReadAllAcknowledgement();
  //     this.scrollToBottom();
  //   } catch (error) {
  //     console.error('Error fetching chat history:', error);
  //   }
  // };

  // retryFetchChatHistory = () => {
  //   if (!this.props.isWebSocketOpen) {
  //     setTimeout(this.retryFetchChatHistory, 1000);
  //   } else {
  //     this.fetchChatHistory()
  //       .then(() => {
  //         this.scrollToBottom();
  //       })
  //       .catch((error) => {
  //         console.error('Error fetching chat history after WebSocket open:', error);
  //       });
  //   }
  // };

  // requestChatHistory = () => {
  //   return new Promise((resolve, reject) => {
  //     if (this.props.isWebSocketOpen) {
  //       const socket = this.props.ws;
  //       const { taskId, familyId, userId } = this.state;

  //       const historyRequest = {
  //         type: 'get_older_history',
  //         family_id: familyId,
  //         user_id: userId,
  //         task_id: taskId
  //       };

  //       const handleMessage = (event) => {
  //         const data = JSON.parse(event.data);
  //         if (data.type === 'chat_history') {
  //           socket.removeEventListener('message', handleMessage);
  //           resolve(data);
  //         }
  //       };

  //       socket.addEventListener('message', handleMessage);
  //       socket.send(JSON.stringify(historyRequest));
  //     } else {
  //       console.error('WebSocket is not open. Waiting before retrying...');
  //       // If not open, wait and try to fetch the chat history again.
  //       setTimeout(() => this.requestChatHistory().then(resolve).catch(reject), 1000);
  //     }
  //   });
  // };

  getFileMetaData = async (attachment) => {
    return {
      file_name: attachment.name,
      file_type: attachment.type,
      file_size: attachment.size
    };
  };

  getSignedUploadUrl = async (fileMetaData) => {
    try {
      if (!this.state.familyId) {
        throw new Error('Missing authentication data (familyId)');
      }

      const response = await fetch(
        `${BASE_URL}/utils/chatUploadUrl.php?action=getSignedUploadUrl`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            familyID: this.state.familyId,
            taskID: this.state.taskId,
            memberID: this.state.memberId,
            fileName: fileMetaData.file_name,
            contentType: fileMetaData.file_type,
            fileSizeBytes: fileMetaData.file_size
          })
        }
      );

      const res = await response.json();

      return {
        ...res.data,
        // used for displaying in chat
        fileMetaData: JSON.stringify(fileMetaData)
      };
    } catch (error) {
      console.log('getSignedUploadUrl - ERROR', error);
      return null;
    }
  };

  uploadToBucket = async (preSignedUrl, attachment) => {
    try {
      const response = await fetch(preSignedUrl, {
        method: 'PUT',
        'Content-Type': attachment.type,
        body: attachment
      });

      return response.ok;
    } catch (error) {
      console.log('uploadToBucket - ERROR: ', error);
      return false;
    }
  };

  getNewSignedUrl = async (objectKey) => {
    try {
      const response = await fetch(`${BASE_URL}/utils/chatFileDownloadUrl.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ objectKey, familyID: this.state.familyId })
      });

      const res = await response.json();
      return res.data.downloadUrl;
    } catch (error) {
      console.log('getNewSignedUrl - ERROR: ', error);
      return null;
    }
  };

  // All logic should be here. Send message is call here
  uploadFile = async (attachment) => {
    const fileMetaData = await this.getFileMetaData(attachment);
    const data = await this.getSignedUploadUrl(fileMetaData);
    if (!data) return false;
    // Upload using signed url
    const uploaded = await this.uploadToBucket(data.uploadUrl, attachment);
    if (uploaded) return data;

    console.log('uploadFile - Error', 'Failed to upload file. Please try again.');
    return false;
  };

  checkSignedUrl = async (file) => {
    // Only get new url if expired
    if (isExpired(file.uploaded_file_signed_expires_at)) {
      return await this.getNewSignedUrl(file.uploaded_file);
    }

    return file.uploaded_file_signed_url;
  };

  // requestOlderChatHistory = () => {
  //   if (this.props.isWebSocketOpen) {
  //     const socket = this.props.ws;
  //     const { taskId, familyId, userId } = this.state;

  //     const offset = this.state.currentOffset;
  //     this.setState((prevState) => ({ currentOffset: prevState.currentOffset + 30 }));
  //     this.setState({ showOldMessages: true });

  //     const historyRequest = {
  //       type: 'get_older_history',
  //       family_id: familyId,
  //       user_id: userId,
  //       task_id: taskId,
  //       offset: offset
  //     };
  //     socket.send(JSON.stringify(historyRequest));
  //   }
  // };

  requestOlderChatHistory = async () => {
    const { taskId, familyId, isSearchMode, searchQuery, searchOffset, currentOffset } = this.state;

    try {
      this.setState({
        showOldMessages: true,
        isLoadingOlder: true
      });

      const offset = isSearchMode ? searchOffset : currentOffset;
      const searchParam = isSearchMode ? searchQuery : null;

      const olderMessages = await fetchOlderChatHistory(familyId, taskId, offset, searchParam);

      if (isSearchMode) {
        this.setState((prevState) => ({
          searchResults: [...olderMessages, ...prevState.searchResults],
          searchOffset: prevState.searchOffset + 30
        }));
      } else {
        this.setState((prevState) => ({
          currentOffset: prevState.currentOffset + 30
        }));
        this.props.onOlderMessagesReceived &&
          this.props.onOlderMessagesReceived({ history: olderMessages });
      }
    } catch (error) {
      console.error('Error fetching older messages:', error);
      this.setState({ errorMessage: 'Failed to load older messages' });
    } finally {
      this.setState({ isLoadingOlder: false });
    }
  };

  sendReadAllAcknowledgement = () => {
    const managingIDs = this.props.managingIDs;
    const ids = managingIDs.map((item) => item.id);

    if (ids.includes(this.state.familyId)) {
      return;
    }

    const all_acknowledgement = {
      type: 'read_all_acknowledgement_db',
      family_id: this.state.familyId,
      task_id: this.state.taskId,
      is_admin: 1
    };
    const { ws: socket } = this.props;
    socket.send(JSON.stringify(all_acknowledgement));
  };

  // handleFileUpload = (file) => {
  //   return new Promise(async (resolve, reject) => {
  //     const formData = new FormData();
  //     formData.append('file', file);
  //     formData.append('familyid', this.state.familyId);

  //     try {
  //       const response = await fetch(`${secureURL}/fileupload/receiveFile.php`, {
  //         method: 'POST',
  //         headers: {
  //           Authorization: `Bearer ${secureKey}`
  //         },
  //         body: formData
  //       });

  //       const data = await response.json();

  //       if (data.success) {
  //         const filePathBase64 = data.path;
  //         const filePath = atob(filePathBase64);
  //         const filePreviewForChat = `${secureURL}/fileupload/${filePath}`;

  //         console.log('File upload successful');
  //         return resolve(filePreviewForChat);
  //       } else {
  //         console.log('File upload failed');
  //         reject(new Error('File upload failed'));
  //       }
  //     } catch (error) {
  //       console.error(error);
  //       reject(error);
  //     }
  //   });
  // };

  handleEditMessageClick = () => {
    this.isEditMessageSend(this.state.userMessage)
      .then(() => {
        this.setState({ userMessage: '' });
      })
      .catch((error) => {
        console.error('Error editing message:', error);
      });
  };

  // OLD WebSocket-based isEditMessageSend - commented out for backup during testing
  /*
  isEditMessageSend = async (message) => {
    this.setState({ isMessageSending: true });

    try {
      const { ws: socket } = this.props;

      const editMessageData = {
        type: 'edit_message',
        family_id: this.state.familyId,
        task_id: this.state.taskId,
        new_message: message,
        message_id: this.state.editingMessageID
      };

      await socket.send(JSON.stringify(editMessageData));
    } catch (error) {
      console.error('Error editing message:', error);
      this.setState({ errorMessage: error.message, isMessageSending: false });
    }
    await this.props.handleEditMessage(this.state.editingMessageID, message);
    this.setState(
      {
        isEditingMessage: false,
        editingMessageID: null,
        isMessageSending: false
      },
      () => {}
    );
  };
  */

  // NEW HTTP-based isEditMessageSend
  isEditMessageSend = async (message) => {
    this.setState({ isMessageSending: true });

    try {
      const response = await editChatMessage({
        family_id: this.state.familyId,
        task_id: this.state.taskId,
        message_id: this.state.editingMessageID,
        new_message: message
      });

      if (response.success) {
        await this.props.handleEditMessage(
          this.state.editingMessageID,
          message,
          response.is_edited
        );

        this.setState({
          isEditingMessage: false,
          editingMessageID: null,
          isMessageSending: false
        });
      } else {
        throw new Error(response.error || 'Failed to edit message');
      }
    } catch (error) {
      console.error('Error editing message:', error);
      this.setState({
        errorMessage: error.message,
        isMessageSending: false
      });
    }
  };

  // OLD WebSocket message confirmation handler - commented out during HTTP migration
  /*
  handleMessageConfirmation = (event) => {
    const data = JSON.parse(event.data);

    if (data.type === 'app_chat_message' && data.message_id === this.state.messageData.message_id) {
      this.props.ws.removeEventListener('message', this.handleMessageConfirmation);
      this.setState(
        {
          showOldMessages: false,
          selectedFile: null,
          fileName: '',
          fileType: '',
          fileSize: '',
          fileTypeForDisplay: '',
          isMessageSending: false,
          messageData: null,
          referencedMessage: null
        },
        () => {
          if (this.state.taskId) {
            this.context.clearDraft(this.state.taskId);
          }
          this.fileInput && (this.fileInput.value = null);
          this.scrollToBottom();
        }
      );
    }
  };
  */

  // OLD WebSocket-based sendMessage - commented out
  /*
  sendMessage = async (message) => {
    this.setState({ isMessageSending: true });

    try {
      let objectKey = null;
      let fileMetadata = null;

      if (this.state.selectedFile) {
        const data = await this.uploadFile(this.state.selectedFile);
        objectKey = data.objectKey;
        fileMetadata = data.fileMetaData;

        if (!data) {
          this.setState({ errorMessage: 'Upload Failed', isMessageSending: false });
          return;
        }
      }
      const messageData = {
        type: 'app_chat_message',
        user_id: this.state.userId,
        family_id: this.state.familyId,
        task_id: this.state.taskId,
        secret: this.isSecretChat(),
        message: message,
        referencedMessage: this.state.referencedMessage ? this.state.referencedMessage.ID : null,
        uploaded_file: objectKey,
        file_metadata: fileMetadata,
        app_version: packageJson.version
      };

      if (this.state.memberId != null) {
        messageData.member_id = this.state.memberId;
      }

      this.setState({ messageData: messageData }, () => {
        this.props.ws.addEventListener('message', this.handleMessageConfirmation);
        this.props.ws.send(JSON.stringify(messageData));
      });
    } catch (error) {
      console.error('Error sending message:', error);
      this.setState({ errorMessage: error.message, isMessageSending: false });
    }
  };
  */

  // NEW HTTP-based sendMessage
  sendMessage = async (message) => {
    this.setState({ isMessageSending: true });

    try {
      let objectKey = null;
      let fileMetadata = null;

      if (this.state.selectedFile) {
        const data = await this.uploadFile(this.state.selectedFile);
        objectKey = data.objectKey;
        fileMetadata = data.fileMetaData;

        if (!data) {
          this.setState({ errorMessage: 'Upload Failed', isMessageSending: false });
          return;
        }
      }

      const messagePayload = {
        family_id: this.state.familyId,
        task_id: this.state.taskId,
        user_id: this.state.userId,
        member_id: this.state.memberId,
        secret: this.isSecretChat(),
        message: message,
        uploaded_file: objectKey,
        file_metadata: fileMetadata
      };

      const response = await sendChatMessage(messagePayload);

      if (response.success && response.message) {
        this.props.onChatMessageReceived && this.props.onChatMessageReceived(response.message);
      }

      this.setState(
        {
          showOldMessages: false,
          selectedFile: null,
          fileName: '',
          fileType: '',
          fileSize: '',
          fileTypeForDisplay: '',
          isMessageSending: false,
          userMessage: '',
          messageData: null,
          referencedMessage: null
        },
        () => {
          if (this.state.taskId) {
            this.context.clearDraft(this.state.taskId);
          }
          this.fileInput && (this.fileInput.value = null);
          this.scrollToBottom();
        }
      );
    } catch (error) {
      console.error('Error sending message:', error);
      this.setState({ errorMessage: error.message, isMessageSending: false });
    }
  };

  handleAddIconClick = () => {
    this.setState({ isChatOptionsOpen: true });
  };

  handleChatOptionClose = () => {
    this.setState({ isChatOptionsOpen: false });
  };

  handleMediaOpen() {
    this.fileInput.accept = 'image/jpeg,image/png,image/gif,image/webp';
    this.fileInput.click();
  }

  handleDocumentOpen = () => {
    this.fileInput.accept =
      'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain,application/rtf,text/csv,text/vcard,application/zip';
    this.fileInput.click();
  };
  bytesToSize = (bytes) => {
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    if (bytes === 0) return '0 Byte';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return (bytes / Math.pow(1024, i)).toFixed(2) + ' ' + sizes[i];
  };

  fileTypeFormatter = (fileType) => {
    return fileTypeMap[fileType] || 'FILE';
  };

  openImageInNewTab = (imageUrl) => {
    window.open(imageUrl, '_blank');
  };

  handleFileSelect = (event) => {
    const file = event.target.files[0];
    const filePreview = file ? URL.createObjectURL(file) : null;
    console.log('handleFileSelect', file);

    this.setState({
      selectedFile: file,
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
      filePreview: filePreview,
      isComposeChatOpen: true,
      isChatOptionsOpen: false
    });
  };

  handleCloseComposeChat = () => {
    this.setState(
      {
        isComposeChatOpen: false,
        selectedFile: null
      },
      () => {
        this.fileInput.value = null;
      }
    );
  };

  handleSelectMessage = (messageData) => {
    this.setState({
      isMessageEditDialogOpen: true,
      selectedMessage: messageData
    });
  };
  handleCloseMessageEdit = () => {
    this.setState({
      isMessageEditDialogOpen: false
    });
  };

  handleReplyMessage = (message) => {
    this.setState({ referencedMessage: message }, () => {
      this.handleCloseMessageEdit();
    });
  };

  handleEditMessage = (message) => {
    this.setState(
      {
        userMessage: message.message,
        isEditingMessage: true,
        editingMessageID: message.ID
      },
      () => {
        this.handleCloseMessageEdit();
      }
    );
  };
  // OLD WebSocket-based handleDeleteMessage - commented out for backup during testing
  /*
  handleDeleteMessage = (selectedMessageId) => {
    if (this.props.isWebSocketOpen) {
      const deleteMessageData = {
        type: 'delete_message',
        member_id: this.state.userId,
        family_id: this.state.familyId,
        task_id: this.state.taskId,
        message_id: selectedMessageId
      };

      this.props.ws.send(JSON.stringify(deleteMessageData));
      this.props.handleDeleteMessage(selectedMessageId);

      this.setState({
        isMessageEditDialogOpen: false
      });
      this.scrollToBottom();
    }
  };
  */

  // NEW HTTP-based handleDeleteMessage
  handleDeleteMessage = async (selectedMessageId) => {
    try {
      const response = await deleteChatMessage({
        family_id: this.state.familyId,
        task_id: this.state.taskId,
        message_id: selectedMessageId
      });

      if (response.success) {
        this.props.handleDeleteMessage(selectedMessageId);

        this.setState({
          isMessageEditDialogOpen: false
        });
      } else {
        throw new Error(response.error || 'Failed to delete message');
      }
    } catch (error) {
      console.error('Error deleting message:', error);
      this.setState({ errorMessage: error.message });
    }
  };

  getNameById = (id) => {
    const entry = this.props.staff.find((entry) => entry && 'ID' in entry && entry.ID === id);
    return entry ? entry.Name : 'user';
  };
  handleInMessageUrl = (text) => {
    const linkStyles = {
      wordBreak: 'break-all'
    };

    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const newlineRegex = /(\n)/g;

    let parts = [];
    text.split(newlineRegex).forEach((part, index) => {
      if (newlineRegex.test(part)) {
        parts.push(<br key={index} />);
      } else {
        let subParts = part.split(urlRegex);
        subParts.forEach((subPart, subIndex) => {
          if (urlRegex.test(subPart)) {
            parts.push(
              <a
                key={`${index}-${subIndex}`}
                href={subPart}
                style={linkStyles}
                target="_blank"
                rel="noopener noreferrer"
              >
                {subPart}
              </a>
            );
          } else {
            parts.push(<span key={`${index}-${subIndex}`}>{subPart}</span>);
          }
        });
      }
    });

    return parts;
  };

  handlePaste = (event) => {
    const items = event.clipboardData.items;
    for (let item of items) {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        const fileTypeFormatted = this.fileTypeFormatter(file.type);
        const fileType = file.type;
        const fileName = file ? file.name : '';
        const fileSize = file ? this.bytesToSize(file.size) : '';
        const filePreview = file ? URL.createObjectURL(file) : null;
        this.setState({
          selectedFile: file,
          fileTypeFormatted: fileTypeFormatted,
          fileName: fileName,
          fileType: fileType,
          fileSize: fileSize,
          filePreview: filePreview,
          isComposeChatOpen: true
        });
        break;
      }
    }
  };

  getMessageById = (id) => {
    return this.props.chatMessages.find((message) => message.ID === id) || null;
  };

  fetchRecommendationData = async (recommendationId) => {
    // Check if already cached or loading
    if (this.state.recommendationCache[recommendationId]) {
      return;
    }

    // Set loading state
    this.setState((prevState) => ({
      recommendationCache: {
        ...prevState.recommendationCache,
        [recommendationId]: { loading: true, data: null }
      }
    }));

    try {
      const response = await fetch(`${BASE_URL}/utils/fetchClientTasks.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          function: 'fetchRecommendationData',
          recommendation_id: recommendationId
        })
      });

      const data = await response.json();

      if (data.error) {
        console.error('Error fetching recommendation:', data.error);
        this.setState((prevState) => ({
          recommendationCache: {
            ...prevState.recommendationCache,
            [recommendationId]: { loading: false, data: null, error: data.error }
          }
        }));
        return;
      }

      // Extract the recommendation data from Airtable record format
      const recommendationData = {
        id: data.id,
        title: data.fields.Title,
        image: data.fields.Images?.[0]?.thumbnails?.small?.url || null
      };

      this.setState((prevState) => ({
        recommendationCache: {
          ...prevState.recommendationCache,
          [recommendationId]: { loading: false, data: recommendationData }
        }
      }));
    } catch (error) {
      console.error('Failed to fetch recommendation data:', error);
      this.setState((prevState) => ({
        recommendationCache: {
          ...prevState.recommendationCache,
          [recommendationId]: { loading: false, data: null, error: error.message }
        }
      }));
    }
  };

  handleRecommendationClick = () => {
    this.setState({ showRecommendationSelection: true });
    // Optionally, if using react-router navigation, you could trigger a route change:
    // this.props.navigate('/RecommendationSelection', { state: { chatTask: this.props.chatTask }});
  };

  handleImageAndFileClick = async (item) => {
    const url = await this.checkSignedUrl(item);

    // if (isPopupBlockerEnabled()) {
    //   return alert('A popup blocker is enabled! Please disable it to proceed.');
    // }
    // console.log(url, '_blank', 'noopener,noreferrer');

    window.open(url, '_blank', 'noopener,noreferrer');
  };

  openImagePreview = async (item) => {
    const url = await this.checkSignedUrl(item);
    this.setState({ imagePreviewUrl: url, imagePreviewItem: item });
  };

  closeImagePreview = () => {
    this.setState({ imagePreviewUrl: null, imagePreviewItem: null });
  };

  handleDownloadImage = async () => {
    const item = this.state.imagePreviewItem;
    if (!item) return;

    try {
      const url = await this.checkSignedUrl(item);
      const fileMetaData = safeJsonParse(item.file_metadata);
      const fileName = fileMetaData?.file_name || 'download';

      const response = await fetch(url, { cache: 'reload' });
      if (!response.ok) throw new Error('Network response was not ok');

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed, falling back to new tab:', error);
      window.open(this.state.imagePreviewUrl, '_blank', 'noopener,noreferrer');
    }
  };

  handleAudioPlay = async (item) => {
    this.setState({ loadingAudioId: item.ID });
    try {
      const url = await this.checkSignedUrl(item);
      return url;
    } finally {
      this.setState({ loadingAudioId: null });
    }
  };

  downloadDocument = async (item) => {
    this.setState({ downloadingDocumentId: item.ID });

    try {
      const url = await this.checkSignedUrl(item);
      const fileMetaData = safeJsonParse(item.file_metadata);
      const fileName = fileMetaData?.file_name || 'download.pdf';

      const response = await fetch(url, { cache: 'reload' });

      if (!response.ok) throw new Error('Network response was not ok');

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download via Blob failed, falling back to new tab:', error);
      const url = await this.checkSignedUrl(item);
      window.open(url, '_blank', 'noopener,noreferrer');
    } finally {
      this.setState({ downloadingDocumentId: null });
    }
  };

  render() {
    const { chatMessages } = this.props;

    const styles = {
      filePreviewDocument: {
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#E0DEDE',
        padding: 6,
        borderRadius: 12,
        width: '100%'
      },
      filePreviewDetails: {
        whiteSpace: 'nowrap',
        textAlign: 'left',
        color: 'white',
        fontSize: 16,
        fontFamily: 'Manrope',
        fontWeight: 'light',
        display: 'flex',
        flexDirection: 'column',
        width: '100%'
      },
      filePreviewFileName: {
        color: 'black',
        fontSize: 16
      },
      filePreviewFileType: {
        color: '#12121299',
        fontSize: 12
      },
      filePreviewFileSize: {
        color: '#12121299',
        fontSize: 12,
        marginRight: 6
      }
    };

    const inputStyles = {
      flexGrow: 1,
      marginRight: '8px'
    };
    const containerStyles = {
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      padding: '16px',
      backgroundColor: 'white',
      borderRadius: '25px',
      margin: '0px 10px 0 10px '
    };
    const messagesContainerStyles = {
      flexGrow: 1,
      overflow: 'auto',
      marginBottom: '10px',
      padding: '10px',
      backgroundColor: 'white'
    };
    const messageStyles = {
      maxWidth: '60%',
      wordWrap: 'break-word',
      borderRadius: '10px',
      padding: '10px'
    };
    const messageContentStyles = {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      color: '#777',
      fontSize: '0.8em'
    };

    const dateStyles = {
      marginLeft: '10px',
      color: '#999',
      fontSize: '10px'
    };
    const submitButton = {
      minWidth: '48px',
      minHeight: '48px',
      background: '#2F98A3',
      borderRadius: '50%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      marginLeft: '4px',
      marginRight: '12px'
    };
    const addIcon = {
      marginLeft: '6',
      marginRight: '12',
      display: 'flex',
      alignItems: 'center'
    };

    const referencedMessageStyle = {
      display: 'flex',
      alignItems: 'center',
      width: '100%'
    };

    const referencedMessageAttachmentStyles = {
      width: '100%',
      borderRadius: '8px',
      marginTop: '8px',
      opacity: '0.5'
    };

    const replyAttachmentStyles = {
      width: '40px',
      height: '40px',
      marginRight: '10px',
      borderRadius: '5px'
    };

    let lastDate = null;
    return (
      <>
        <Container maxWidth="xl" style={containerStyles}>
          {/* Overlay for editing mode */}
          {this.state.editingMessageID && (
            <div
              style={{
                position: 'fixed', // Changed from 'absolute' to 'fixed'
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 2
              }}
              onClick={(e) => {
                e.stopPropagation();
                this.setState({ editingMessageID: null, isEditingMessage: false, userMessage: '' });
              }}
            ></div>
          )}

          {this.state.showRecommendationSelection ? (
            <div>
              <IconButton
                onClick={() => this.setState({ showRecommendationSelection: false })}
                style={{ marginBottom: '16px' }}
              >
                <ChevronLeft style={{ marginRight: '8px' }} />
              </IconButton>
              <RecommendationSelection
                chatTask={this.props.chatTask}
                familyId={this.props.familyID}
              />
            </div>
          ) : (
            <>
              {' '}
              {this.state.taskId != null ? (
                <>
                  <div style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '16px',
                        position: 'relative'
                      }}
                    >
                      <Typography variant="h6" style={{ margin: 0 }}>
                        {this.state.isSecretTask && (
                          <span
                            style={{
                              marginRight: '10px',
                              padding: '4px 8px',
                              background: '#FF6B6B',
                              color: 'white',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: 'bold'
                            }}
                          >
                            SECRET
                          </span>
                        )}
                        {this.state.taskId === 'General'
                          ? 'General Chat'
                          : `Chat for the Task - ${this.props.chatTask?.fields?.['Title']}`}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={this.toggleSearchInput}
                        style={{ position: 'absolute', right: 0 }}
                      >
                        <SearchIcon style={{ color: '#555', fontSize: 22 }} />
                      </IconButton>
                    </div>
                    {this.props.chatTask && this.props.chatTask.recLink && (
                      <div style={{ paddingBottom: '16px' }}>
                        <Button
                          style={{
                            backgroundColor: 'black',
                            color: 'white',
                            borderRadius: 8,
                            padding: '8px 16px',
                            fontFamily: 'Manrope',
                            fontSize: 16,
                            textTransform: 'none'
                          }}
                          onClick={() => {
                            this.handleRecommendationClick(this.props.chatTask.recLink);
                          }}
                        >
                          Go to recommendations
                        </Button>
                      </div>
                    )}
                    <Divider style={{ marginBottom: '16px' }} />
                  </div>
                  <Paper elevation={0} style={messagesContainerStyles}>
                    {this.state.isComposeChatOpen && (
                      <ComposeChatDialog
                        isComposeChatOpen={this.state.isComposeChatOpen}
                        onClose={this.handleCloseComposeChat}
                        userMessage={this.state.userMessage}
                        selectedFile={this.state.selectedFile}
                        fileName={this.state.fileName}
                        fileType={this.state.fileType}
                        fileSize={this.state.fileSize}
                        filePreview={this.state.filePreview}
                        fileTypeFormatted={this.state.fileTypeFormatted}
                        sendMessage={this.sendMessage}
                        image={this.state.image}
                        video={this.state.video}
                        document={this.state.document}
                      />
                    )}

                    {this.state.isMessageEditDialogOpen && (
                      <MessageEditDialog
                        isMessageEditDialogOpen={this.state.isMessageEditDialogOpen}
                        onClose={this.handleCloseMessageEdit}
                        handleDeleteMessage={this.handleDeleteMessage}
                        message={this.state.selectedMessage}
                        handleReplyMessage={this.handleReplyMessage}
                        handleEditMessage={this.handleEditMessage}
                        isChatArchived={this.props.chatTask?.isArchived}
                      />
                    )}

                    {this.state.showSearchInput && (
                      <div
                        style={{
                          position: 'sticky',
                          top: 0,
                          zIndex: 10,
                          padding: '10px 16px'
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: this.state.isSearchMode ? '8px' : '0',
                            gap: '8px'
                          }}
                        >
                          <TextField
                            size="small"
                            placeholder="Search messages..."
                            value={this.state.searchQuery}
                            onChange={this.handleSearchChange}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                this.handleSearchSubmit();
                              }
                            }}
                            disabled={this.state.isSearching}
                            autoFocus
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon style={{ color: '#777', fontSize: 20 }} />
                                </InputAdornment>
                              ),
                              endAdornment: this.state.searchQuery ? (
                                <InputAdornment position="end">
                                  <IconButton
                                    size="small"
                                    onClick={this.handleClearSearch}
                                    disabled={this.state.isSearching}
                                  >
                                    <ClearIcon style={{ fontSize: 18 }} />
                                  </IconButton>
                                </InputAdornment>
                              ) : null,
                              style: {
                                borderRadius: '20px',
                                backgroundColor: '#f5f5f5',
                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)'
                              }
                            }}
                            style={{ maxWidth: '400px', width: '100%' }}
                          />
                        </div>
                        {this.state.isSearchMode && (
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '8px'
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                padding: '4px 12px',
                                borderRadius: '12px',
                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)'
                              }}
                            >
                              <Typography
                                variant="caption"
                                style={{ color: '#777', fontFamily: 'Manrope' }}
                              >
                                Showing {this.state.searchResults.length} results for: "
                                {this.state.searchQuery}"
                              </Typography>
                              <Button
                                size="small"
                                onClick={this.handleClearSearch}
                                style={{
                                  textTransform: 'none',
                                  fontFamily: 'Manrope',
                                  fontSize: '12px',
                                  color: '#2F98A3',
                                  padding: '2px 8px',
                                  minWidth: 'auto'
                                }}
                              >
                                Back to chat
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* {this.props.isWebSocketOpen && (
                      <div
                        onClick={this.requestOlderChatHistory}
                        style={{
                          justifyContent: 'center',
                          alignItems: 'center',
                          display: 'flex',
                          marginLeft: 12,
                          marginRight: 12
                        }}
                      >
                        <div
                          style={{
                            color: 'black',
                            padding: '6px 16px 6px 16px',
                            fontSize: 14,
                            fontFamily: 'Manrope',
                            fontWeight: '500',
                            wordWrap: 'break-word',
                            background: '#EFEEED',
                            borderRadius: 12
                          }}
                        >
                          Fetch Older Messages
                        </div>
                      </div>
                    )} */}

                    {(!this.state.isSearchMode || this.state.searchResults.length > 0) && (
                      <div
                        onClick={this.requestOlderChatHistory}
                        style={{
                          justifyContent: 'center',
                          alignItems: 'center',
                          display: 'flex',
                          marginLeft: 12,
                          marginRight: 12
                        }}
                      >
                        <div
                          style={{
                            color: 'black',
                            padding: '6px 16px 6px 16px',
                            fontSize: 14,
                            fontFamily: 'Manrope',
                            fontWeight: '500',
                            wordWrap: 'break-word',
                            background: '#EFEEED',
                            borderRadius: 12
                          }}
                        >
                          {this.state.isSearchMode ? 'Fetch More Results' : 'Fetch Older Messages'}
                        </div>
                      </div>
                    )}

                    {(this.state.isSearchMode ? this.state.searchResults : chatMessages).map(
                      (messageData, index) => {
                        const { user_id, member_id } = messageData;
                        const isSentByCurrentUser = this.props.staff.some(
                          (member) => member.ID === String(user_id)
                        );
                        const relevantId =
                          String(user_id) === String(this.state.userId) ? user_id : member_id;
                        const name = isSentByCurrentUser
                          ? this.getNameById(user_id) || relevantId
                          : this.props.members.find((member) => member.ID === relevantId)?.Name ||
                            'User';

                        // Fetch recommendation data if not in cache
                        if (
                          messageData.recommendation_id &&
                          !this.state.recommendationCache[messageData.recommendation_id]
                        ) {
                          this.fetchRecommendationData(messageData.recommendation_id);
                        }
                        const cachedData =
                          this.state.recommendationCache[messageData.recommendation_id];
                        const isLoading = !cachedData || cachedData.loading;
                        const recommendationData = cachedData?.data;

                        // don't render delete messages by assistant
                        if (messageData.hidden && isSentByCurrentUser) {
                          return null;
                        }

                        // --- DATE CALCULATION LOGIC ---
                        const messageDate = parseMessageTimestamp(messageData.timestamp);
                        let displayDate = null;

                        if (messageDate) {
                          const messageDay = messageDate.toDateString();

                          if (lastDate !== messageDay) {
                            displayDate = formatDateDivider(messageDate);
                            lastDate = messageDay;
                          }
                        }

                        return (
                          <React.Fragment key={messageData.ID || index}>
                            {/* Conditionally render the date divider */}
                            {displayDate && (
                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  margin: '16px 0'
                                }}
                              >
                                <div
                                  style={{
                                    padding: '6px 16px',
                                    background: '#EFEEED',
                                    borderRadius: 12,
                                    color: 'rgba(0, 0, 0, 0.70)',
                                    fontSize: 14,
                                    fontFamily: 'Manrope',
                                    fontWeight: '500'
                                  }}
                                >
                                  {displayDate}
                                </div>
                              </div>
                            )}
                            <ChatMessage
                              key={messageData.ID}
                              senderName={name}
                              item={messageData}
                              isReceived={!isSentByCurrentUser}
                              onPressMessage={() => {
                                if (isSentByCurrentUser) {
                                  this.handleSelectMessage(messageData);
                                }
                              }}
                              onPressImage={() => this.openImagePreview(messageData)}
                              onPressRecommendation={() => {
                                this.handleRecommendationClick(this.props.chatTask.recLink);
                              }}
                              onPressDocument={() => this.downloadDocument(messageData)}
                              documentLoading={this.state.downloadingDocumentId === messageData.ID}
                              onPressAudio={() => this.handleAudioPlay(messageData)}
                              audioLoading={this.state.loadingAudioId === messageData.ID}
                              recommendationData={recommendationData}
                              recommendationLoading={isLoading}
                            />
                          </React.Fragment>
                        );

                        // const { member_id, user_id } = messageData;
                        // const messageTimestamp = messageData.timestamp;
                        // const messageDate = new Date(messageTimestamp * 1000);
                        // const messageDay = messageDate.toDateString();
                        // let file_type = null;
                        // let file_name = null;
                        // let file_size = null;
                        // if (messageData.file_metadata && messageData.file_metadata !== 'undefined') {
                        //   const fileMetadataObject = JSON.parse(messageData.file_metadata);
                        //   file_name = fileMetadataObject?.file_name;
                        //   file_type = fileMetadataObject?.file_type;
                        //   file_size = fileMetadataObject?.file_size;
                        // }
                        // let displayDate = null;
                        // if (lastDate !== messageDay) {
                        //   const today = new Date().toDateString();
                        //   const yesterday = new Date(Date.now() - 86400000).toDateString();
                        //   if (messageDay === today) {
                        //     displayDate = 'Today';
                        //   } else if (messageDay === yesterday) {
                        //     displayDate = 'Yesterday';
                        //   } else {
                        //     displayDate =
                        //       messageDate.toDateString().slice(0, 3) +
                        //       ' ' +
                        //       messageDate.getDate() +
                        //       ' ' +
                        //       messageDate.toLocaleString('default', { month: 'short' });
                        //   }
                        //   lastDate = messageDay;
                        // }
                        // const relevantId =
                        //   String(user_id) === String(this.state.userId) ? user_id : member_id;
                        // const isSentByCurrentUser = this.props.staff.some(
                        //   (member) => member.ID === String(user_id)
                        // );
                        // const fileTypeForDisplay = this.fileTypeFormatter(file_type);
                        // const messageParts = this.handleInMessageUrl(messageData.message);
                        // // const isMessageRead = messageData.timestamp_read || messageData.admin_timestamp_read;
                        // const isMessageRead = messageData.admin_timestamp_read;
                        // if (messageData.hidden && isSentByCurrentUser) {
                        //   return null;
                        // }
                        // const name = isSentByCurrentUser
                        //   ? this.getNameById(user_id) || relevantId
                        //   : this.props.members.find((member) => member.ID === relevantId)?.Name ||
                        //     'User';
                        // return (
                        //   <div
                        //     key={index}
                        //     style={{ display: 'flex', flexDirection: 'column', marginBottom: '10px' }}
                        //   >
                        //     {displayDate && (
                        //       <div
                        //         key={index}
                        //         style={{
                        //           justifyContent: 'center',
                        //           alignItems: 'center',
                        //           display: 'flex',
                        //           margin: 'auto',
                        //           padding: '6px 16px 16px 16px',
                        //           maxWidth: 150
                        //         }}
                        //       >
                        //         <div
                        //           style={{
                        //             padding: '6px 16px 6px 16px',
                        //             background: '#EFEEED',
                        //             borderRadius: 12,
                        //             justifyContent: 'center',
                        //             alignItems: 'center',
                        //             display: 'flex'
                        //           }}
                        //         >
                        //           <div
                        //             style={{
                        //               color: 'rgba(0, 0, 0, 0.70)',
                        //               fontSize: 14,
                        //               fontFamily: 'Manrope',
                        //               fontWeight: '500',
                        //               wordWrap: 'break-word'
                        //             }}
                        //           >
                        //             {displayDate}
                        //           </div>
                        //         </div>
                        //       </div>
                        //     )}
                        //     <div
                        //       onClick={(e) => {
                        //         if (e.target.tagName.toLowerCase() === 'a') {
                        //           return;
                        //         }
                        //         //TODO: Enable message options for client messages later
                        //         // Only allow opening dialog for messages sent by current user (admin)
                        //         if (isSentByCurrentUser) {
                        //           this.handleSelectMessage(messageData);
                        //         }
                        //       }}
                        //       style={{
                        //         display: 'flex',
                        //         justifyContent: isSentByCurrentUser ? 'flex-end' : 'flex-start',
                        //         margin: '5px',
                        //         padding: '10px',
                        //         zIndex: messageData.ID === this.state.editingMessageID ? 600 : 1
                        //       }}
                        //     >
                        //       {messageData.emoji && (
                        //         <div
                        //           style={{
                        //             // position: 'absolute',
                        //             top: -20,
                        //             left: -20,
                        //             padding: 4,
                        //             display: 'flex',
                        //             alignItems: 'center',
                        //             fontSize: '24px'
                        //           }}
                        //         >
                        //           <span>{messageData.emoji}</span>
                        //         </div>
                        //       )}
                        //       <div
                        //         style={{
                        //           ...messageStyles,
                        //           backgroundColor: isSentByCurrentUser ? '#E1F7FA' : '#EFEEED'
                        //         }}
                        //       >
                        //         {messageData.referencedMessage &&
                        //           this.getMessageById(messageData.referencedMessage) && (
                        //             <div
                        //               style={{
                        //                 backgroundColor: 'rgba(0, 0, 0, 0.05)',
                        //                 borderRadius: '8px',
                        //                 padding: '8px',
                        //                 marginBottom: '8px',
                        //                 fontSize: '14px',
                        //                 width: '90%',
                        //                 fontStyle: 'italic'
                        //               }}
                        //             >
                        //               {/* For an attachedmnt in the Referenced Message */}
                        //               {(() => {
                        //                 const referencedMessage = this.getMessageById(
                        //                   messageData.referencedMessage
                        //                 );
                        //                 if (referencedMessage.file_metadata) {
                        //                   const fileMetadata = JSON.parse(
                        //                     referencedMessage.file_metadata
                        //                   );
                        //                   const fileTypeForDisplay = this.fileTypeFormatter(
                        //                     fileMetadata.file_type
                        //                   );
                        //                   if (this.state.image.includes(fileMetadata.file_type)) {
                        //                     return (
                        //                       <img
                        //                         src={referencedMessage.uploaded_file}
                        //                         alt={fileMetadata.file_name}
                        //                         style={referencedMessageAttachmentStyles}
                        //                       />
                        //                     );
                        //                   } else if (
                        //                     this.state.video.includes(fileMetadata.file_type)
                        //                   ) {
                        //                     return (
                        //                       <video
                        //                         controls
                        //                         src={referencedMessage.uploaded_file}
                        //                         style={referencedMessageAttachmentStyles}
                        //                       />
                        //                     );
                        //                   } else if (fileTypeForDisplay === 'PDF') {
                        //                     return (
                        //                       <a
                        //                         href={messageData.uploaded_file}
                        //                         target="_blank"
                        //                         rel="noopener noreferrer"
                        //                         style={{
                        //                           display: 'flex',
                        //                           alignItems: 'center',
                        //                           textDecoration: 'none',
                        //                           color: 'inherit'
                        //                         }}
                        //                       >
                        //                         <PictureAsPdfIcon
                        //                           style={{
                        //                             fontSize: 28,
                        //                             marginRight: 12
                        //                           }}
                        //                         />
                        //                         <span>{fileMetadata.file_name}</span>
                        //                       </a>
                        //                     );
                        //                   } else if (
                        //                     this.state.document.includes(fileMetadata.file_type)
                        //                   ) {
                        //                     return (
                        //                       <a
                        //                         href={referencedMessage.uploaded_file}
                        //                         target="_blank"
                        //                         rel="noopener noreferrer"
                        //                         style={{
                        //                           display: 'flex',
                        //                           textDecoration: 'none',
                        //                           color: 'inherit',
                        //                           marginTop: '8px',
                        //                           opacity: '0.5'
                        //                         }}
                        //                       >
                        //                         <div style={styles.filePreviewDocument}>
                        //                           <img
                        //                             src="./app-icons/document.svg"
                        //                             alt="Document Icon"
                        //                             style={{ width: 48, marginRight: 6 }}
                        //                           />
                        //                           <div style={styles.filePreviewDetails}>
                        //                             <span style={styles.filePreviewFileName}>
                        //                               {fileMetadata.file_name}
                        //                             </span>
                        //                             <div>
                        //                               <span style={styles.filePreviewFileSize}>
                        //                                 {fileMetadata.file_size}
                        //                               </span>
                        //                               <span style={{ marginRight: 6 }}>&middot;</span>
                        //                               <span style={styles.filePreviewFileType}>
                        //                                 {fileTypeForDisplay}
                        //                               </span>
                        //                             </div>
                        //                           </div>
                        //                         </div>
                        //                       </a>
                        //                     );
                        //                   }
                        //                 }
                        //                 return null;
                        //               })()}
                        //               {this.getMessageById(messageData.referencedMessage).message}
                        //             </div>
                        //           )}
                        //         {messageData.recommendationData && (
                        //           <div
                        //             style={{
                        //               borderRadius: '8px',
                        //               marginTop: '8px',
                        //               cursor: 'pointer'
                        //             }}
                        //             onClick={
                        //               null
                        //               //     () => {
                        //               //     // Navigate to the RecommendationView with the specific recommendation
                        //               //     this.context.navigate('/recommendations', {
                        //               //             state: { chatTask: chatTask}
                        //               //     });
                        //               // }
                        //             }
                        //           >
                        //             <Box
                        //               sx={{
                        //                 borderLeft: '2px solid #848891',
                        //                 marginBottom: '8px',
                        //                 paddingLeft: '8px'
                        //               }}
                        //             >
                        //               <Typography
                        //                 variant="subtitle2"
                        //                 sx={{
                        //                   fontWeight: 'bold',
                        //                   fontSize: 14,
                        //                   fontFamily: "'Open Sans', sans-serif"
                        //                 }}
                        //               >
                        //                 {messageData.recommendationData.Title}
                        //               </Typography>
                        //               <Typography
                        //                 variant="subtitle2"
                        //                 sx={{ fontFamily: "'Open Sans', sans-serif" }}
                        //               >
                        //                 {messageData.recommendationData.priceRange}
                        //               </Typography>
                        //             </Box>
                        //             {messageData.uploaded_file &&
                        //               messageData.uploaded_file.length > 0 && (
                        //                 <img
                        //                   src={messageData.uploaded_file}
                        //                   alt={messageData.recommendationData.Title}
                        //                   style={{ maxWidth: '100%', height: 'auto' }}
                        //                 />
                        //               )}
                        //           </div>
                        //         )}
                        //         <div style={messageContentStyles}>
                        //           <span
                        //             style={{
                        //               fontWeight: 'Bold',
                        //               color: isSentByCurrentUser ? '#009FC2' : '#5b5b5b'
                        //             }}
                        //           >
                        //             {name}
                        //           </span>
                        //         </div>
                        //         <div>
                        //           {messageData.hidden ? (
                        //             !isSentByCurrentUser ? (
                        //               <s>{messageParts}</s>
                        //             ) : null
                        //           ) : (
                        //             <span>{messageParts}</span>
                        //           )}
                        //           {file_type && (
                        //             <div
                        //               style={{ margin: '6px 0' }}
                        //               onClick={(e) => e.stopPropagation()}
                        //             >
                        //               {this.state.image.includes(file_type) && (
                        //                 <div
                        //                   onClick={() =>
                        //                     this.openImageInNewTab(messageData.uploaded_file)
                        //                   }
                        //                 >
                        //                   <img
                        //                     style={{
                        //                       width: '100%',
                        //                       height: '100%',
                        //                       borderRadius: '10px'
                        //                     }}
                        //                     src={messageData.uploaded_file}
                        //                     alt={file_name}
                        //                   />
                        //                 </div>
                        //               )}
                        //               {this.state.video.includes(file_type) && (
                        //                 <div>
                        //                   <video
                        //                     style={{
                        //                       width: '100%',
                        //                       height: '100%',
                        //                       borderRadius: '10px'
                        //                     }}
                        //                     controls
                        //                     src={messageData.uploaded_file}
                        //                   />
                        //                 </div>
                        //               )}
                        //               {fileTypeForDisplay === 'PDF' && (
                        //                 <a
                        //                   href={messageData.uploaded_file}
                        //                   target="_blank"
                        //                   rel="noopener noreferrer"
                        //                   style={{
                        //                     display: 'flex',
                        //                     textDecoration: 'none',
                        //                     color: 'inherit'
                        //                   }}
                        //                 >
                        //                   <div style={styles.filePreviewDocument}>
                        //                     <PictureAsPdfIcon
                        //                       style={{
                        //                         color: 'black',
                        //                         fontSize: 48,
                        //                         marginRight: 12
                        //                       }}
                        //                     />
                        //                     <div style={styles.filePreviewDetails}>
                        //                       <span style={styles.filePreviewFileName}>
                        //                         {file_name}
                        //                       </span>
                        //                       <div>
                        //                         <span style={styles.filePreviewFileSize}>
                        //                           {file_size}
                        //                         </span>
                        //                         <span style={{ marginRight: 6 }}>&middot;</span>
                        //                         <span style={styles.filePreviewFileType}>
                        //                           {fileTypeForDisplay}
                        //                         </span>
                        //                       </div>
                        //                     </div>
                        //                   </div>
                        //                 </a>
                        //               )}
                        //               {this.state.document.includes(file_type) && (
                        //                 <a
                        //                   href={messageData.uploaded_file}
                        //                   target="_blank"
                        //                   rel="noopener noreferrer"
                        //                   style={{
                        //                     display: 'flex',
                        //                     textDecoration: 'none',
                        //                     color: 'inherit'
                        //                   }}
                        //                 >
                        //                   <div style={styles.filePreviewDocument}>
                        //                     <img
                        //                       src="/app-icons/document.svg"
                        //                       alt="Document Icon"
                        //                       style={{
                        //                         width: 48,
                        //                         marginRight: 6,
                        //                         filter: 'invert(100%)'
                        //                       }}
                        //                     />
                        //                     <div style={styles.filePreviewDetails}>
                        //                       <span style={styles.filePreviewFileName}>
                        //                         {file_name}
                        //                       </span>
                        //                       <div>
                        //                         <span style={styles.filePreviewFileSize}>
                        //                           {file_size}
                        //                         </span>
                        //                         <span style={{ marginRight: 6 }}>&middot;</span>
                        //                         <span style={styles.filePreviewFileType}>
                        //                           {fileTypeForDisplay}
                        //                         </span>
                        //                       </div>
                        //                     </div>
                        //                   </div>
                        //                 </a>
                        //               )}
                        //             </div>
                        //           )}
                        //           {/* Recommendation rendering */}
                        //           {messageData.recommendation_id && (() => {
                        //             // Fetch recommendation data if not in cache
                        //             if (!this.state.recommendationCache[messageData.recommendation_id]) {
                        //               this.fetchRecommendationData(messageData.recommendation_id);
                        //             }
                        //             const cachedData = this.state.recommendationCache[messageData.recommendation_id];
                        //             const isLoading = !cachedData || cachedData.loading;
                        //             const recommendationData = cachedData?.data;
                        //             return (
                        //               <div style={{ margin: '6px 0' }} onClick={(e) => e.stopPropagation()}>
                        //                 <div
                        //                   style={{
                        //                     display: 'flex',
                        //                     flexDirection: 'row',
                        //                     alignItems: 'center',
                        //                     backgroundColor: 'rgba(0, 0, 0, 0.05)',
                        //                     paddingTop: 6,
                        //                     paddingBottom: 6,
                        //                     paddingLeft: 6,
                        //                     paddingRight: 10,
                        //                     borderRadius: 6,
                        //                     marginBottom: 10,
                        //                     borderLeft: '3px solid #313131',
                        //                     cursor: 'pointer',
                        //                   }}
                        //                   onClick={() => {
                        //                     // Handle recommendation click - could open details modal
                        //                     console.log('Recommendation clicked:', messageData.recommendation_id);
                        //                   }}
                        //                 >
                        //                   {isLoading ? (
                        //                     <>
                        //                       <div style={{
                        //                         width: 40,
                        //                         height: 40,
                        //                         borderRadius: 6,
                        //                         marginRight: 8,
                        //                         backgroundColor: 'rgba(0, 0, 0, 0.1)',
                        //                       }} />
                        //                       <div style={{ flexShrink: 1 }}>
                        //                         <div style={{ fontSize: 14, color: '#535353' }}>Recommendation</div>
                        //                         <div style={{
                        //                           width: 150,
                        //                           height: 15,
                        //                           backgroundColor: 'rgba(0, 0, 0, 0.05)',
                        //                           borderRadius: 4,
                        //                           marginTop: 2,
                        //                         }} />
                        //                       </div>
                        //                     </>
                        //                   ) : recommendationData ? (
                        //                     <>
                        //                       {recommendationData.image && (
                        //                         <img
                        //                           style={{
                        //                             width: 40,
                        //                             height: 40,
                        //                             borderRadius: 6,
                        //                             marginRight: 8,
                        //                             objectFit: 'cover',
                        //                             backgroundColor: 'rgba(0, 0, 0, 0.05)',
                        //                           }}
                        //                           src={recommendationData.image}
                        //                           alt={recommendationData.title || 'Recommendation'}
                        //                         />
                        //                       )}
                        //                       <div style={{ flexShrink: 1 }}>
                        //                         <div style={{ fontSize: 14, color: '#535353' }}>Recommendation</div>
                        //                         <div style={{
                        //                           fontSize: 14,
                        //                           fontWeight: 600,
                        //                           color: '#292929',
                        //                           overflow: 'hidden',
                        //                           textOverflow: 'ellipsis',
                        //                           whiteSpace: 'nowrap',
                        //                         }}>
                        //                           {recommendationData.title}
                        //                         </div>
                        //                       </div>
                        //                     </>
                        //                   ) : (
                        //                     <div style={{ fontSize: 14, color: '#535353', fontStyle: 'italic' }}>
                        //                       Failed to load recommendation
                        //                     </div>
                        //                   )}
                        //                 </div>
                        //               </div>
                        //             );
                        //           })()}
                        //           <span style={dateStyles}>
                        //             {new Date(messageData.timestamp * 1000).toLocaleTimeString([], {
                        //               hour: '2-digit',
                        //               minute: '2-digit'
                        //             })}
                        //             {isSentByCurrentUser && (
                        //               <span style={{ marginLeft: '2px' }}>
                        //                 {messageData.timestamp && (
                        //                   <img
                        //                     alt={isMessageRead ? 'Message Read' : 'Message Sent'}
                        //                     src="/app-icons/msg-read.svg"
                        //                     style={{
                        //                       filter: isMessageRead
                        //                         ? 'invert(48%) sepia(79%) saturate(2476%) hue-rotate(86deg) brightness(100%) contrast(119%)'
                        //                         : 'invert(50%)'
                        //                     }}
                        //                   />
                        //                 )}
                        //               </span>
                        //             )}
                        //           </span>
                        //         </div>
                        //       </div>
                        //     </div>
                        //   </div>
                        // );
                      }
                    )}

                    <div ref={this.messagesEndRef} />

                    {this.state.errorMessage && (
                      <ErrorModal
                        message={this.state.errorMessage}
                        onClose={() => this.setState({ errorMessage: null })}
                      />
                    )}

                    {this.state.imagePreviewUrl && (
                      <ImagePreviewModal
                        src={this.state.imagePreviewUrl}
                        onClose={this.closeImagePreview}
                        onDownload={this.handleDownloadImage}
                      />
                    )}
                  </Paper>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      zIndex: this.state.editingMessageID ? 600 : 1,
                      backgroundColor: this.state.editingMessageID ? 'white' : null
                    }}
                  >
                    {!this.props.chatTask?.isArchived && (
                      <>
                        <ChatOptionsDialog
                          isChatOptionsOpen={this.state.isChatOptionsOpen}
                          onClose={this.handleChatOptionClose}
                          userMessage={this.state.userMessage}
                          handleMediaOpen={this.handleMediaOpen}
                          handleDocumentOpen={this.handleDocumentOpen}
                        />
                        <div style={referencedMessageStyle}>
                          {this.state.referencedMessage && (
                            <div
                              style={{
                                backgroundColor: '#f0f0f0',
                                padding: '10px',
                                margin: '5px 0',
                                borderRadius: '10px',
                                width: `${this.inputRef.current.clientWidth - 35}px`,
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis'
                              }}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between'
                                }}
                              >
                                {(() => {
                                  const referencedMessage = this.getMessageById(
                                    this.state.referencedMessage.ID
                                  );
                                  if (referencedMessage.file_metadata) {
                                    const fileMetadata = JSON.parse(
                                      referencedMessage.file_metadata
                                    );
                                    const fileTypeForDisplay = this.fileTypeFormatter(
                                      fileMetadata.file_type
                                    );

                                    if (this.state.image.includes(fileMetadata.file_type)) {
                                      return (
                                        <img
                                          src={referencedMessage.uploaded_file}
                                          alt={fileMetadata.file_name}
                                          style={replyAttachmentStyles}
                                        />
                                      );
                                    } else if (fileTypeForDisplay === 'PDF') {
                                      return (
                                        <>
                                          <PictureAsPdfIcon
                                            style={{
                                              fontSize: 28,
                                              marginRight: 12
                                            }}
                                          />
                                          <Typography>{fileMetadata.file_name}</Typography>
                                        </>
                                      );
                                    } else if (
                                      this.state.document.includes(fileMetadata.file_type)
                                    ) {
                                      return (
                                        <img
                                          src="./app-icons/document.svg"
                                          alt="Document Icon"
                                          style={replyAttachmentStyles}
                                        />
                                      );
                                    }
                                  }
                                  return null;
                                })()}
                                <Typography noWrap>
                                  {this.state.referencedMessage.message}
                                </Typography>
                                <button
                                  style={{
                                    border: 'none',
                                    background: 'none',
                                    cursor: 'pointer',
                                    color: '#333'
                                  }}
                                  onClick={() => this.setState({ referencedMessage: null })}
                                >
                                  <CloseIcon />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                        <div
                          ref={this.inputRef}
                          style={{ display: 'flex', alignItems: 'center', width: '100%' }}
                        >
                          <div style={addIcon} onClick={() => this.handleAddIconClick()}>
                            <img alt="Add" src="/app-icons/add.svg" />
                          </div>

                          {this.props.isWebSocketOpen ? (
                            <TextField
                              style={inputStyles}
                              placeholder="Message"
                              value={this.state.userMessage}
                              fullWidth
                              multiline
                              variant="outlined"
                              InputProps={{
                                endAdornment: this.state.isMessageSending ? (
                                  <InputAdornment position="end">
                                    <CircularProgress size={20} style={{ color: '#2F98A3' }} />
                                  </InputAdornment>
                                ) : null,
                                style: { color: 'black' }
                              }}
                              inputProps={{
                                style: { padding: 0 }
                              }}
                              onPaste={this.handlePaste}
                              onChange={(e) => {
                                this.setState({ userMessage: e.target.value });
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && e.shiftKey) {
                                  const currentValue = this.state.userMessage;
                                  const selectionStart = e.target.selectionStart;

                                  this.setState({
                                    userMessage:
                                      currentValue.substring(0, selectionStart) +
                                      '\n' +
                                      currentValue.substring(selectionStart)
                                  });

                                  e.preventDefault();
                                } else if (e.key === 'Enter' && !e.shiftKey) {
                                  e.preventDefault();
                                  if (this.state.userMessage.trim() !== '') {
                                    if (this.state.isEditingMessage) {
                                      this.handleEditMessageClick();
                                    } else {
                                      this.sendMessage(this.state.userMessage).catch((error) => {
                                        console.error('Error sending message:', error);
                                      });
                                      this.setState({ userMessage: '' });
                                    }
                                  }
                                }
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                ...inputStyles,
                                padding: '18px',
                                width: '100%',
                                color: '#0D0D0D',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              Chat Loading. Please wait.
                            </div>
                          )}

                          <FormControl>
                            <input
                              type="file"
                              id="chat-file-input"
                              style={{ display: 'none' }}
                              onChange={this.handleFileSelect}
                              ref={(input) => {
                                this.fileInput = input;
                              }}
                            />
                          </FormControl>

                          {this.state.isEditingMessage && this.state.userMessage !== '' ? (
                            <Button
                              style={{
                                ...submitButton,
                                visibility: 'visible',
                                pointerEvents: 'auto',
                                color: 'white'
                              }}
                              type="button"
                              onClick={this.handleEditMessageClick}
                            >
                              <CheckIcon />
                            </Button>
                          ) : this.state.isEditingMessage && this.state.userMessage === '' ? (
                            <Button
                              style={{
                                ...submitButton,
                                visibility: 'visible',
                                pointerEvents: 'auto',
                                color: 'white'
                              }}
                              type="button"
                              onClick={() => {
                                this.setState({ isEditingMessage: false, editingMessageID: null });
                              }}
                            >
                              <CloseIcon />
                            </Button>
                          ) : (
                            <Button
                              style={submitButton}
                              type="submit"
                              disabled={
                                !this.props.isWebSocketOpen || this.state.userMessage.trim() === ''
                              }
                              onClick={() => {
                                if (this.state.userMessage.trim() !== '') {
                                  this.sendMessage(this.state.userMessage).catch((error) => {
                                    console.error('Error sending message:', error);
                                  });
                                  this.setState({ userMessage: '' });
                                }
                              }}
                            >
                              <img
                                alt="Send"
                                src="/app-icons/send.svg"
                                style={{
                                  filter: !this.props.isWebSocketOpen ? 'grayscale(100%)' : 'none',
                                  opacity: !this.props.isWebSocketOpen ? '0.5' : '1'
                                }}
                              />
                            </Button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </>
              ) : (
                'Select a task to chat'
              )}
            </>
          )}
        </Container>
      </>
    );
  }
}

export default Chat;
